import React from 'react';
import { CreateJourneyMethodType } from './widget';
import { useUpdateEffect } from 'react-use';
import { useSpring, useSpringRef, animated, easings } from '@react-spring/web';
import classNames from 'classnames';
import { CreateJourneyPanelContent, CreateJourneyTabType } from './panel-content';
import { FormikProps } from 'formik';

export const ENTER_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const LEAVE_ANIMATION_CONFIG = {
  duration: 200,
  easing: easings.easeOutCubic,
};

export const CreateJourneyPanel = ({
  animate,
  type,
  onSubmit,
  onClose,
}: {
  animate: boolean;
  type: CreateJourneyMethodType;
  onSubmit: (tab: CreateJourneyTabType) => (formProps: FormikProps<Record<string, any>>) => void;
  onClose: () => void;
}) => {
  const pageAnimationApi = useSpringRef();
  const contentAnimationApi = useSpringRef();

  const [pageAnimationStyle] = useSpring(() => ({
    ref: pageAnimationApi,
    from: { opacity: 0 },
    to: { opacity: 1 },
  }));

  const [contentAnimationStyle] = useSpring(() => ({
    ref: contentAnimationApi,
    from: { y: 40, opacity: 0 },
    to: { y: 0, opacity: 1 },
  }));

  useUpdateEffect(() => {
    if (!animate) {
      contentAnimationApi.start({ y: 40, opacity: 0, config: LEAVE_ANIMATION_CONFIG });
      pageAnimationApi.start({ opacity: 0, config: LEAVE_ANIMATION_CONFIG, delay: 200 });

      return;
    }
    pageAnimationApi.start({ opacity: 1, config: ENTER_ANIMATION_CONFIG });
    contentAnimationApi.start({
      y: 0,
      opacity: 1,
      delay: 200,
      config: ENTER_ANIMATION_CONFIG,
    });
  }, [animate]);

  const isTypeTemplate = type === 'template';

  return (
    <div className='journey-theme-dark fixed pointer-events-none inset-0 z-neue-create-modal'>
      <animated.div className='h-full w-full bg-neue-journey-bg' style={pageAnimationStyle}>
        <div className={classNames('h-screen w-screen overflow-y-auto', { 'pointer-events-auto': type !== null })}>
          <animated.div
            className={classNames('relative h-full w-full flex flex-col gap-y-10 pb-24 pt-32 text-neue-journey-fg', {
              'px-[5.5rem]': !isTypeTemplate,
              'overflow-y-auto pl-[5.5rem] pr-32': isTypeTemplate,
            })}
            style={contentAnimationStyle}
          >
            <CreateJourneyPanelContent type={type} onSubmit={onSubmit} onClose={onClose} />
          </animated.div>
        </div>
      </animated.div>
    </div>
  );
};
